
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  getAllRecruitmentCandidates,
  getCandidatesByStatus,
  getRecruitmentStats,
  searchCandidates,
  updateCandidateStatus,
  type RecruitmentCandidate
} from '@/lib/real-data-service'
import { recruitmentApi } from '@/api/recruitment'
import {
  UserPlus,
  Search,
  Filter,
  Plus,
  Mail,
  Phone,
  Calendar,
  FileText,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  MessageSquare,
  Download,
  ExternalLink,
  AlertTriangle,
  TrendingUp,
  Users,
  Target,
  Zap,
  Send,
  Paperclip,
  MoreHorizontal,
  Sparkles,
  Loader2,
  BrainCircuit,
  Award,
  FileCheck,
  DollarSign,
  Gift,
  Calendar as CalendarIcon,
  MapPin,
  Clock as ClockIcon
} from 'lucide-react'
import { usePageTitle } from '@/stores/page-title-store'

interface CandidateCardProps {
  candidate: RecruitmentCandidate
  onViewCandidate: (candidate: RecruitmentCandidate) => void
  onUpdateStatus: (candidateId: string, status: RecruitmentCandidate['status']) => void
}

function CandidateCard({ candidate, onViewCandidate, onUpdateStatus }: CandidateCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied':
        return 'bg-gray-500/20 text-gray-400'
      case 'screening':
        return 'bg-info/20 text-info'
      case 'interview':
        return 'bg-warning/20 text-warning'
      case 'technical':
        return 'bg-brand-primary/20 text-brand-primary'
      case 'approved':
        return 'bg-success/20 text-success'
      case 'rejected':
        return 'bg-error/20 text-error'
      default:
        return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      applied: 'Candidatura',
      screening: 'Triagem',
      interview: 'Entrevista',
      technical: 'Técnica',
      approved: 'Aprovado',
      rejected: 'Rejeitado'
    }
    return statusMap[status] || status
  }

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'linkedin':
        return ''
      case 'headhunter':
        return ''
      case 'referral':
        return ''
      case 'website':
        return ''
      default:
        return ''
    }
  }

  return (
    <div className="card-hover">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center mr-4">
            <span className="text-lg font-bold text-brand-dark">
              {candidate.name.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-white mb-1">
              {candidate.name}
            </h3>
            <p className="text-brand-gray text-sm">{candidate.position}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(candidate.status)}`}>
            {getStatusText(candidate.status)}
          </span>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-sm text-gray-300">
          <Mail className="text-brand-primary mr-2" size={16} />
          <span>{candidate.email}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-300">
          <Phone className="text-brand-primary mr-2" size={16} />
          <span>{candidate.phone}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-300">
          <span className="text-lg mr-2">{getSourceIcon(candidate.source)}</span>
          <span className="capitalize">{candidate.source}</span>
        </div>
        
        {candidate.evaluation?.score && (
          <div className="flex items-center text-sm text-gray-300">
            <Star className="text-warning mr-2" size={16} />
            <span>Avaliação: {candidate.evaluation.score}/10</span>
          </div>
        )}
      </div>

      {candidate.evaluation?.skills && (
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-2">Habilidades:</p>
          <div className="flex flex-wrap gap-1">
            {candidate.evaluation.skills.slice(0, 3).map((skill: any, index: number) => (
              <span
                key={index}
                className="px-2 py-1 bg-brand-dark text-xs text-brand-primary rounded-monumenta"
              >
                {skill.name} ({skill.level}/10)
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-700">
        <div className="flex space-x-2">
          <button
            onClick={() => onViewCandidate(candidate)}
            className="text-xs text-brand-primary hover:text-brand-primary/80"
          >
            Ver Detalhes
          </button>
          <button className="text-xs text-brand-gray hover:text-white">
            Agendar Entrevista
          </button>
        </div>
        
        <select
          value={candidate.status}
          onChange={(e) => onUpdateStatus(candidate.id, e.target.value as 'applied' | 'screening' | 'interview' | 'technical' | 'approved' | 'rejected')}
          className="text-xs bg-brand-dark border border-gray-700 rounded px-2 py-1 text-white"
        >
          <option value="applied">Candidatura</option>
          <option value="screening">Triagem</option>
          <option value="interview">Entrevista</option>
          <option value="technical">Técnica</option>
          <option value="approved">Aprovado</option>
          <option value="rejected">Rejeitado</option>
        </select>
      </div>
    </div>
  )
}

// Proposal Modal Component
function ProposalModal({ candidate, isOpen, onClose }: { candidate: RecruitmentCandidate | null, isOpen: boolean, onClose: () => void }) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [proposal, setProposal] = useState<any>(null)
  const [salary, setSalary] = useState('12000')
  const [startDate, setStartDate] = useState('2024-02-01')
  
  const handleGenerateProposal = () => {
    setIsGenerating(true)
    
    // Simular geração de proposta com IA
    setTimeout(() => {
      const generatedProposal = {
        position: 'Redator Senior',
        salary: `R$ ${Number(salary).toLocaleString('pt-BR')},00`,
        benefits: [
          'Vale Refeição: R$ 1.200/mês',
          'Vale Transporte',
          'Plano de Saúde Unimed',
          'Plano Odontológico',
          'Gympass',
          'Day Off no aniversário',
          'Licença maternidade/paternidade estendida',
          'Participação nos Lucros e Resultados (PLR)'
        ],
        workModel: 'Híbrido - 3x presencial, 2x home office',
        location: 'São Paulo - SP',
        schedule: '9h às 18h (flexibilidade de 1h)',
        probation: '3 meses',
        vacation: '30 dias + 1/3 constitucional',
        description: `Estamos muito empolgados em oferecer a você a oportunidade de se juntar à nossa equipe como Redator Senior na Monumenta Live Marketing. Com base em sua experiência e fit cultural demonstrado durante o processo seletivo, acreditamos que você será uma adição valiosa para nossa equipe criativa.

Sua expertise em redação criativa e live marketing será fundamental para liderar projetos de grande impacto e mentorar nossa equipe de redação. Você terá a oportunidade de trabalhar com marcas renomadas e participar de ativações inovadoras que marcam presença no mercado.`,
        responsibilities: [
          'Liderar a criação de campanhas de live marketing de grande impacto',
          'Desenvolver conceitos criativos, slogans e scripts complexos',
          'Supervisionar toda a parte textual das campanhas',
          'Participar ativamente de reuniões com clientes',
          'Mentorar e liderar a equipe de redação',
          'Apresentar propostas criativas de alto nível para clientes',
          'Participar de eventos garantindo qualidade da entrega'
        ],
        nextSteps: [
          'Confirmação de aceite até 15/01/2024',
          'Envio de documentos para admissão',
          'Início em 01/02/2024',
          'Programa de onboarding de 2 semanas'
        ]
      }
      
      setProposal(generatedProposal)
      setIsGenerating(false)
    }, 4000)
  }
  
  if (!isOpen || !candidate) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Proposal Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <FileCheck className="text-white" size={20} />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-white">
                Proposta de Emprego
              </h2>
              <p className="text-gray-400 text-sm">Para: {candidate?.name || ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>

        {!proposal && !isGenerating && (
          <div className="space-y-6">
            {/* Configuration */}
            <div className="bg-brand-dark p-6 rounded-monumenta">
              <h3 className="font-display text-lg font-semibold text-white mb-4">
                Configurar Proposta
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Salário Base (R$):</label>
                  <input
                    type="number"
                    value={salary}
                    onChange={(e) => setSalary(e.target.value)}
                    className="form-input w-full"
                    min="8000"
                    max="25000"
                    step="500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Data de Início:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
              </div>
            </div>

            {/* AI Generation */}
            <div className="bg-brand-primary/10 border border-brand-primary/20 rounded-monumenta p-6">
              <div className="flex items-center mb-4">
                <Sparkles className="text-brand-primary mr-3" size={24} />
                <div>
                  <h3 className="font-display text-lg font-semibold text-brand-primary">
                    Gerador de Proposta com IA
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Nossa IA criará uma proposta personalizada baseada no perfil do candidato
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleGenerateProposal}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                <BrainCircuit size={20} />
                <span>Gerar Proposta Personalizada</span>
              </button>
            </div>
          </div>
        )}

        {isGenerating && (
          <div className="space-y-6">
            <div className="bg-brand-dark p-6 rounded-monumenta border border-brand-primary/20">
              <div className="flex items-center space-x-3 mb-4">
                <Loader2 className="animate-spin text-brand-primary" size={24} />
                <span className="text-white font-semibold">IA gerando proposta personalizada...</span>
              </div>
              <div className="space-y-3">
                <p className="text-gray-400 text-sm">• Analisando perfil salarial do mercado</p>
                <p className="text-gray-400 text-sm">• Comparando com posições similares</p>
                <p className="text-gray-400 text-sm">• Calculando pacote de benefícios competitivo</p>
                <p className="text-gray-400 text-sm">• Personalizando termos baseado na experiência</p>
                <p className="text-gray-400 text-sm">• Formatando documento final</p>
              </div>
              
              <div className="mt-4">
                <div className="progress-bar">
                  <div className="progress-fill animate-pulse" style={{ width: '75%' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {proposal && (
          <div className="space-y-6">
            {/* Proposal Header */}
            <div className="bg-gradient-to-r from-green-600/20 to-brand-primary/20 p-6 rounded-monumenta border border-green-500/30">
              <div className="text-center">
                <h3 className="font-display text-2xl font-bold text-white mb-2">
                  Proposta de Emprego
                </h3>
                <p className="text-gray-300">
                  <strong>{proposal.position}</strong> • Monumenta Live Marketing
                </p>
              </div>
            </div>

            {/* Salary & Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-brand-dark p-6 rounded-monumenta">
                <div className="flex items-center mb-4">
                  <DollarSign className="text-green-500 mr-3" size={24} />
                  <h4 className="font-display text-lg font-semibold text-white">
                    Remuneração
                  </h4>
                </div>
                <div className="text-3xl font-bold text-green-500 mb-2">
                  {proposal.salary}
                </div>
                <p className="text-gray-400 text-sm">Salário base mensal (CLT)</p>
              </div>

              <div className="bg-brand-dark p-6 rounded-monumenta">
                <div className="flex items-center mb-4">
                  <Gift className="text-brand-primary mr-3" size={24} />
                  <h4 className="font-display text-lg font-semibold text-white">
                    Benefícios
                  </h4>
                </div>
                <ul className="space-y-2">
                  {proposal.benefits.slice(0, 4).map((benefit: string, index: number) => (
                    <li key={index} className="text-gray-300 text-sm">
                      • {benefit}
                    </li>
                  ))}
                  <li className="text-brand-primary text-sm">
                    + {proposal.benefits.length - 4} outros benefícios
                  </li>
                </ul>
              </div>
            </div>

            {/* Work Details */}
            <div className="bg-brand-dark p-6 rounded-monumenta">
              <h4 className="font-display text-lg font-semibold text-white mb-4">
                Detalhes da Posição
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center space-x-3">
                  <MapPin className="text-brand-primary" size={16} />
                  <div>
                    <p className="text-gray-400 text-xs">Local</p>
                    <p className="text-white text-sm">{proposal.location}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <ClockIcon className="text-brand-primary" size={16} />
                  <div>
                    <p className="text-gray-400 text-xs">Horário</p>
                    <p className="text-white text-sm">{proposal.schedule}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="text-brand-primary" size={16} />
                  <div>
                    <p className="text-gray-400 text-xs">Modelo</p>
                    <p className="text-white text-sm">{proposal.workModel}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-300 text-sm">{proposal.description}</p>
            </div>

            {/* Responsibilities */}
            <div className="bg-brand-dark p-6 rounded-monumenta">
              <h4 className="font-display text-lg font-semibold text-white mb-4">
                Principais Responsabilidades
              </h4>
              <ul className="space-y-2">
                {proposal.responsibilities.map((resp: string, index: number) => (
                  <li key={index} className="text-gray-300 text-sm">
                    • {resp}
                  </li>
                ))}
              </ul>
            </div>

            {/* Next Steps */}
            <div className="bg-brand-primary/10 border border-brand-primary/20 p-6 rounded-monumenta">
              <h4 className="font-display text-lg font-semibold text-brand-primary mb-4">
                Próximos Passos
              </h4>
              <ol className="space-y-2">
                {proposal.nextSteps.map((step: string, index: number) => (
                  <li key={index} className="text-gray-300 text-sm">
                    {index + 1}. {step}
                  </li>
                ))}
              </ol>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setProposal(null)
                  setIsGenerating(false)
                }}
                className="btn-secondary"
              >
                Editar Proposta
              </button>
              
              <div className="flex space-x-3">
                <button className="btn-secondary">
                  Salvar Rascunho
                </button>
                <button
                  onClick={() => {
                    // Simular envio
                    setTimeout(() => {
                      alert('Proposta enviada com sucesso!')
                      onClose()
                    }, 1000)
                  }}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Send size={16} />
                  <span>Enviar Proposta</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Email Modal Component
function EmailModal({ candidate, isOpen, onClose }: { candidate: RecruitmentCandidate | null, isOpen: boolean, onClose: () => void }) {
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  
  const generateEmailContent = () => {
    if (!candidate) return
    
    setEmailSubject(`Oportunidade: Redator Senior - Monumenta Live Marketing`)
    setEmailBody(`Olá ${candidate.name},

Espero que esteja bem!

Gostaria de conversar com você sobre uma oportunidade como Redator Senior aqui na Monumenta Live Marketing. Analisamos seu perfil e acreditamos que suas habilidades são um grande match para nossa equipe criativa.

A vaga envolve:
• Liderar a criação de campanhas de live marketing de grande impacto
• Desenvolver conceitos criativos, slogans e scripts complexos
• Supervisionar toda a parte textual das campanhas
• Participar ativamente de reuniões com clientes
• Mentorar a equipe de redação

Teria interesse em conversarmos mais sobre esta oportunidade? Podemos agendar uma conversa para esta semana.

Aguardo seu retorno!

Atenciosamente,
Equipe de Recrutamento
Monumenta Live Marketing`)
  }

  if (!isOpen || !candidate) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Email Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <Mail className="text-white" size={20} />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-white">
                Novo E-mail
              </h2>
              <p className="text-gray-400 text-sm">Para: {candidate?.name || ''}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>

        {/* Email Form */}
        <div className="space-y-4">
          {/* Recipients */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Para:</label>
            <input
              type="email"
              value={candidate?.email || ''}
              readOnly
              className="form-input w-full bg-gray-800"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Assunto:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Digite o assunto do e-mail..."
              className="form-input w-full"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Mensagem:</label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Digite sua mensagem..."
              rows={12}
              className="form-input w-full resize-none"
            />
          </div>

          {/* AI Suggestion */}
          <div className="bg-brand-primary/10 border border-brand-primary/20 rounded-monumenta p-4">
            <div className="flex items-center mb-3">
              <Sparkles className="text-brand-primary mr-2" size={16} />
              <span className="text-sm font-medium text-brand-primary">Sugestão de IA</span>
            </div>
            <p className="text-gray-300 text-sm mb-3">
              Detectamos que este candidato tem experiência em redação criativa. 
              Que tal mencionar nossos projetos recentes com marcas de destaque?
            </p>
            <button
              onClick={generateEmailContent}
              className="text-xs text-brand-primary hover:text-brand-primary/80"
            >
              Usar sugestão da IA
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-700">
            <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 text-gray-400 hover:text-white">
                <Paperclip size={16} />
                <span className="text-sm">Anexar arquivo</span>
              </button>
              <button className="flex items-center space-x-2 text-gray-400 hover:text-white">
                <MoreHorizontal size={16} />
                <span className="text-sm">Mais opções</span>
              </button>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  // Simular envio
                  setTimeout(() => {
                    alert('E-mail enviado com sucesso!')
                    onClose()
                  }, 1000)
                }}
                className="btn-primary flex items-center space-x-2"
              >
                <Send size={16} />
                <span>Enviar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CandidateModal({ candidate, isOpen, onClose, onOpenEmail, onOpenProposal }: { candidate: RecruitmentCandidate | null, isOpen: boolean, onClose: () => void, onOpenEmail: (candidate: RecruitmentCandidate) => void, onOpenProposal: (candidate: RecruitmentCandidate) => void }) {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [aiAnalysis, setAiAnalysis] = useState<any>(null)
  
  const handleAIAnalysis = () => {
    setIsAnalyzing(true)
    
    // Simular análise de IA
    setTimeout(() => {
      const analysis = {
        overallScore: 8.5,
        strengths: [
          'Experiência sólida em redação criativa para campanhas de marketing',
          'Portfolio diversificado com projetos de diferentes segmentos',
          'Conhecimento em live marketing e ativações de marca',
          'Habilidades de liderança e mentoria demonstradas'
        ],
        concerns: [
          'Falta experiência específica com eventos de grande porte',
          'Portfólio não inclui campanhas de tecnologia'
        ],
        recommendation: 'RECOMENDADO PARA ENTREVISTA',
        matchScore: 85,
        skills: [
          { name: 'Redação Criativa', level: 9, match: 95 },
          { name: 'Estratégia de Comunicação', level: 8, match: 88 },
          { name: 'Live Marketing', level: 7, match: 82 },
          { name: 'Liderança de Equipe', level: 8, match: 90 },
          { name: 'Apresentação para Clientes', level: 7, match: 85 }
        ],
        suggestedQuestions: [
          'Como você abordaria a criação de um conceito para uma ativação de marca em um evento de 50.000 pessoas?',
          'Descreva uma situação onde teve que mentorar um redator júnior. Como foi o processo?',
          'Qual foi o projeto de live marketing mais desafiador que já trabalhou?'
        ]
      }
      
      setAiAnalysis(analysis)
      setIsAnalyzing(false)
    }, 3000)
  }
  
  if (!isOpen || !candidate) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl font-bold text-white">
            {candidate?.name || ''}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="font-display text-lg font-semibold text-brand-primary mb-3">
              Informações Básicas
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">E-mail:</span>
                <p className="text-white">{candidate.email}</p>
              </div>
              <div>
                <span className="text-gray-400">Telefone:</span>
                <p className="text-white">{candidate.phone}</p>
              </div>
              <div>
                <span className="text-gray-400">Cargo:</span>
                <p className="text-white">{candidate.position}</p>
              </div>
              <div>
                <span className="text-gray-400">Origem:</span>
                <p className="text-white capitalize">{candidate.source}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div>
            <h3 className="font-display text-lg font-semibold text-brand-primary mb-3">
              Documentos
            </h3>
            <div className="space-y-2">
              {candidate.documents.resume && (
                <div className="flex items-center justify-between p-3 bg-brand-dark rounded-monumenta">
                  <div className="flex items-center">
                    <FileText className="text-brand-primary mr-2" size={16} />
                    <span className="text-white">Currículo</span>
                  </div>
                  <button className="text-brand-primary hover:text-brand-primary/80">
                    <Download size={16} />
                  </button>
                </div>
              )}
              {candidate.documents.portfolio && (
                <div className="flex items-center justify-between p-3 bg-brand-dark rounded-monumenta">
                  <div className="flex items-center">
                    <ExternalLink className="text-brand-primary mr-2" size={16} />
                    <span className="text-white">Portfólio</span>
                  </div>
                  <button className="text-brand-primary hover:text-brand-primary/80">
                    <ExternalLink size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Evaluation */}
          {candidate.evaluation && (
            <div>
              <h3 className="font-display text-lg font-semibold text-brand-primary mb-3">
                Avaliação
              </h3>
              <div className="space-y-4">
                {candidate.evaluation.score && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white">Nota Geral</span>
                      <span className="text-brand-primary font-bold">
                        {candidate.evaluation.score}/10
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${candidate.evaluation.score * 10}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {candidate.evaluation.notes && (
                  <div>
                    <span className="text-gray-400 block mb-2">Observações:</span>
                    <p className="text-white bg-brand-dark p-3 rounded-monumenta">
                      {candidate.evaluation.notes}
                    </p>
                  </div>
                )}

                {candidate.evaluation.skills && (
                  <div>
                    <span className="text-gray-400 block mb-2">Habilidades Avaliadas:</span>
                    <div className="space-y-2">
                      {candidate.evaluation.skills.map((skill: any, index: number) => (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white text-sm">{skill.name}</span>
                            <span className="text-brand-primary text-sm">
                              {skill.level}/10
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${skill.level * 10}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {candidate.evaluation.interviewer && (
                  <div className="text-sm text-gray-400">
                    Entrevistado por: <span className="text-white">{candidate.evaluation.interviewer}</span>
                    {candidate.evaluation.interviewDate && (
                      <> em {new Date(candidate.evaluation.interviewDate).toLocaleDateString('pt-BR')}</>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Analysis Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-brand-primary">
                Análise de IA
              </h3>
              {!aiAnalysis && (
                <button
                  onClick={handleAIAnalysis}
                  disabled={isAnalyzing}
                  className="btn-primary flex items-center space-x-2 text-sm"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      <span>Analisando...</span>
                    </>
                  ) : (
                    <>
                      <BrainCircuit size={16} />
                      <span>Analisar Currículo</span>
                    </>
                  )}
                </button>
              )}
            </div>
            
            {isAnalyzing && (
              <div className="bg-brand-dark p-4 rounded-monumenta border border-brand-primary/20">
                <div className="flex items-center space-x-3 mb-3">
                  <Loader2 className="animate-spin text-brand-primary" size={20} />
                  <span className="text-white">IA analisando o currículo...</span>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm">• Extraindo informações do currículo</p>
                  <p className="text-gray-400 text-sm">• Comparando com requisitos da vaga de Redator Senior</p>
                  <p className="text-gray-400 text-sm">• Calculando compatibilidade de habilidades</p>
                  <p className="text-gray-400 text-sm">• Gerando recomendações personalizadas</p>
                </div>
              </div>
            )}
            
            {aiAnalysis && (
              <div className="space-y-4">
                {/* Overall Score */}
                <div className="bg-brand-dark p-4 rounded-monumenta">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-semibold">Compatibilidade Geral</span>
                    <div className="flex items-center space-x-2">
                      <Award className="text-warning" size={16} />
                      <span className="text-brand-primary font-bold text-lg">
                        {aiAnalysis.matchScore}%
                      </span>
                    </div>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${aiAnalysis.matchScore}%` }}
                    ></div>
                  </div>
                  <p className={`text-sm mt-2 font-semibold ${
                    aiAnalysis.recommendation === 'RECOMENDADO PARA ENTREVISTA' 
                      ? 'text-success' 
                      : 'text-warning'
                  }`}>
                    {aiAnalysis.recommendation}
                  </p>
                </div>
                
                {/* Skills Analysis */}
                <div className="bg-brand-dark p-4 rounded-monumenta">
                  <h4 className="text-white font-semibold mb-3">Análise de Habilidades</h4>
                  <div className="space-y-3">
                    {aiAnalysis.skills.map((skill: any, index: number) => (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-300 text-sm">{skill.name}</span>
                          <span className="text-brand-primary text-sm">
                            {skill.match}% match
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${skill.match}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Strengths & Concerns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-success/10 border border-success/20 p-4 rounded-monumenta">
                    <h4 className="text-success font-semibold mb-2">Pontos Fortes</h4>
                    <ul className="space-y-1">
                      {aiAnalysis.strengths.map((strength: string, index: number) => (
                        <li key={index} className="text-gray-300 text-sm">• {strength}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-warning/10 border border-warning/20 p-4 rounded-monumenta">
                    <h4 className="text-warning font-semibold mb-2">Pontos de Atenção</h4>
                    <ul className="space-y-1">
                      {aiAnalysis.concerns.map((concern: string, index: number) => (
                        <li key={index} className="text-gray-300 text-sm">• {concern}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Suggested Questions */}
                <div className="bg-brand-primary/10 border border-brand-primary/20 p-4 rounded-monumenta">
                  <h4 className="text-brand-primary font-semibold mb-2">Perguntas Sugeridas para Entrevista</h4>
                  <ul className="space-y-2">
                    {aiAnalysis.suggestedQuestions.map((question: string, index: number) => (
                      <li key={index} className="text-gray-300 text-sm">
                        {index + 1}. {question}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          
          {/* Job Description */}
          <div>
            <h3 className="font-display text-lg font-semibold text-brand-primary mb-3">
              Descrição da Vaga: Redator Senior
            </h3>
            <div className="bg-brand-dark p-4 rounded-monumenta space-y-3">
              <p className="text-gray-300 text-sm">
                Redator é responsável por criar conceitos e desenvolver textos persuasivos para 
                campanhas, ativações de live marketing, materiais de marketing e conteúdos em diversos formatos. 
                Ele colabora com o Diretor de Arte e a equipe criativa para transformar as ideias em mensagens 
                impactantes e claras, que reflitam os valores da marca e engajem o público-alvo.
              </p>
              
              <div>
                <h4 className="text-white font-semibold mb-2">Funções:</h4>
                <p className="text-gray-300 text-sm">
                  Liderar a criação de campanhas, ativações e experiências live marketing de grande impacto, 
                  desenvolver conceitos criativos, slogans e scripts complexos, além de supervisionar toda a 
                  parte textual das campanhas. O redator sênior participa ativamente de reuniões com clientes 
                  e lidera a equipe de redação.
                </p>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-2">Responsabilidades:</h4>
                <p className="text-gray-300 text-sm">
                  Garantir a qualidade e originalidade dos textos criados, assegurar que a mensagem transmitida 
                  esteja alinhada com os objetivos estratégicos da marca e do cliente. Ele também é responsável 
                  por mentorar a equipe e apresentar propostas criativas de alto nível para os clientes. Estar à 
                  frente de apresentações de projetos e estratégias para os clientes e também a participação em 
                  eventos, garantindo a execução e a qualidade da entrega.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-4 border-t border-gray-700">
            <button className="btn-primary">
              Agendar Entrevista
            </button>
            <button 
              onClick={() => onOpenEmail(candidate)}
              className="btn-secondary"
            >
              Enviar E-mail
            </button>
            <button 
              onClick={() => onOpenProposal(candidate)}
              className="btn-secondary"
            >
              Gerar Proposta
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function RecruitmentStats({ recruitmentStats, totalCandidates, isLoading }: { recruitmentStats: any, totalCandidates: number, isLoading: boolean }) {
  
  const stats = useMemo(() => {
    if (isLoading) {
      return [
        { label: 'Candidatos Ativos', value: '-', icon: Users, color: 'text-brand-primary' },
        { label: 'Em Entrevista', value: '-', icon: MessageSquare, color: 'text-warning' },
        { label: 'Aprovados', value: '-', icon: CheckCircle, color: 'text-success' },
        { label: 'Taxa de Conversão', value: '-', icon: TrendingUp, color: 'text-info' }
      ]
    }

    return [
      {
        label: 'Candidatos Ativos',
        value: totalCandidates,
        icon: Users,
        color: 'text-brand-primary'
      },
      {
        label: 'Em Entrevista',
        value: recruitmentStats.byStatus.interview || 0,
        icon: MessageSquare,
        color: 'text-warning'
      },
      {
        label: 'Aprovados',
        value: recruitmentStats.byStatus.approved || 0,
        icon: CheckCircle,
        color: 'text-success'
      },
      {
        label: 'Taxa de Conversão',
        value: `${recruitmentStats.conversionRate}%`,
        icon: TrendingUp,
        color: 'text-info'
      }
    ]
  }, [recruitmentStats, totalCandidates, isLoading])

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {stats.map((stat, index) => {
        const Icon = stat.icon
        return (
          <div key={index} className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Icon className={stat.color} size={20} />
            </div>
            <div className={`text-xl sm:text-2xl font-display font-bold ${stat.color} mb-1`}>
              {stat.value}
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wider leading-tight">
              {stat.label}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function RecruitmentContent() {
  const { user } = useAuth()
  usePageTitle('Recrutamento')
  const [candidates, setCandidates] = useState<RecruitmentCandidate[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedCandidate, setSelectedCandidate] = useState<RecruitmentCandidate | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailCandidate, setEmailCandidate] = useState<RecruitmentCandidate | null>(null)
  const [proposalModalOpen, setProposalModalOpen] = useState(false)
  const [proposalCandidate, setProposalCandidate] = useState<RecruitmentCandidate | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recruitmentStats, setRecruitmentStats] = useState<any>({})
  const [jobPostings, setJobPostings] = useState<any[]>([])

  // Load recruitment data on component mount
  useEffect(() => {
    const loadRecruitmentData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Load candidates and stats (mock data)
        const [candidatesData, statsData] = await Promise.all([
          getAllRecruitmentCandidates(),
          getRecruitmentStats()
        ])

        setCandidates(candidatesData)
        setRecruitmentStats(statsData)

        // Load job postings from API
        const postingsResult = await recruitmentApi.listJobPostings(true)
        setJobPostings(postingsResult.items || [])
      } catch (err) {
        console.error('Error loading recruitment data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load recruitment data')
      } finally {
        setIsLoading(false)
      }
    }

    loadRecruitmentData()
  }, [])

  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           candidate.position.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [candidates, searchTerm, statusFilter])

  if (!user) {
    return null
  }

  const handleViewCandidate = (candidate: RecruitmentCandidate) => {
    setSelectedCandidate(candidate)
    setModalOpen(true)
  }
  
  const handleOpenEmail = (candidate: RecruitmentCandidate) => {
    setEmailCandidate(candidate)
    setEmailModalOpen(true)
    setModalOpen(false)
  }
  
  const handleOpenProposal = (candidate: RecruitmentCandidate) => {
    setProposalCandidate(candidate)
    setProposalModalOpen(true)
    setModalOpen(false)
  }

  const handleUpdateStatus = async (candidateId: string, newStatus: RecruitmentCandidate['status']) => {
    try {
      // Update in the real data service
      await updateCandidateStatus(candidateId, newStatus)

      // Update local state
      setCandidates(prevCandidates =>
        prevCandidates.map(candidate =>
          candidate.id === candidateId
            ? { ...candidate, status: newStatus }
            : candidate
        )
      )
    } catch (err) {
      console.error('Error updating candidate status:', err)
      // Optionally show error message to user
    }
  }

  if (isLoading) {
    return (
      
                <div className="space-y-4 sm:space-y-6">

                  {/* Loading State */}
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <h3 className="font-display text-lg font-medium text-white mb-2">
                      Carregando candidatos...
                    </h3>
                    <p className="text-brand-gray max-w-md mx-auto">
                      Buscando informações do processo seletivo
                    </p>
                  </div>
                </div>
              
    )
  }

  if (error) {
    return (
      
                <div className="space-y-4 sm:space-y-6">

                  {/* Error State */}
                  <div className="text-center py-12">
                    <div className="text-brand-red mb-4">
                      <AlertTriangle className="mx-auto h-12 w-12" />
                    </div>
                    <h3 className="font-display text-lg font-medium text-white mb-2">
                      Erro ao carregar candidatos
                    </h3>
                    <p className="text-brand-gray max-w-md mx-auto mb-4">
                      {error}
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="btn-primary"
                    >
                      Tentar Novamente
                    </button>
                  </div>
                </div>
              
    )
  }

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end">
          <button
            onClick={() => window.location.href = '/recruitment/new'}
            className="btn-primary flex items-center justify-center gap-2"
          >
            <Plus size={20} />
            <span>Nova Candidatura</span>
          </button>
        </div>

        {/* Stats */}
        <RecruitmentStats
          recruitmentStats={recruitmentStats}
          totalCandidates={candidates.length}
          isLoading={isLoading}
        />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={20} />
            <input
              type="text"
              placeholder="Buscar candidatos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-full pl-10"
              style={{ paddingLeft: '3rem' }}
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input w-full sm:w-auto"
          >
            <option value="all">Todos os Status</option>
            <option value="applied">Candidatura</option>
            <option value="screening">Triagem</option>
            <option value="interview">Entrevista</option>
            <option value="technical">Técnica</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Rejeitado</option>
          </select>
        </div>

        {/* Pipeline Visualization */}
        <div className="card">
          <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-4">
            Pipeline de Candidatos
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {[
              { status: 'applied', label: 'Candidatura', count: candidates.filter(c => c.status === 'applied').length },
              { status: 'screening', label: 'Triagem', count: candidates.filter(c => c.status === 'screening').length },
              { status: 'interview', label: 'Entrevista', count: candidates.filter(c => c.status === 'interview').length },
              { status: 'technical', label: 'Técnica', count: candidates.filter(c => c.status === 'technical').length },
              { status: 'approved', label: 'Aprovado', count: candidates.filter(c => c.status === 'approved').length },
              { status: 'rejected', label: 'Rejeitado', count: candidates.filter(c => c.status === 'rejected').length }
            ].map((stage, index) => (
              <div
                key={stage.status}
                className="text-center p-3 sm:p-4 bg-brand-dark rounded-monumenta border border-gray-700"
              >
                <div className="text-xl sm:text-2xl font-bold text-brand-primary mb-1">
                  {stage.count}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">
                  {stage.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Results Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-400 space-y-1 sm:space-y-0">
          <span>
            {filteredCandidates.length} candidato{filteredCandidates.length !== 1 ? 's' : ''} encontrado{filteredCandidates.length !== 1 ? 's' : ''}
          </span>
          <span className="text-xs sm:text-sm">
            Ordenado por data de aplicação
          </span>
        </div>

        {/* Candidates Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredCandidates.map(candidate => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              onViewCandidate={handleViewCandidate}
              onUpdateStatus={handleUpdateStatus}
            />
          ))}
        </div>

        {/* Empty State */}
        {filteredCandidates.length === 0 && (
          <div className="text-center py-12">
            <UserPlus className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              Nenhum candidato encontrado
            </h3>
            <p className="text-gray-400 mb-4">
              Tente ajustar os filtros ou termos de busca
            </p>
            <button 
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
              }}
              className="btn-secondary"
            >
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Candidate Modal */}
      <CandidateModal
        candidate={selectedCandidate}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onOpenEmail={handleOpenEmail}
        onOpenProposal={handleOpenProposal}
      />
      
      {/* Email Modal */}
      <EmailModal
        candidate={emailCandidate}
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
      />
      
      {/* Proposal Modal */}
      <ProposalModal
        candidate={proposalCandidate}
        isOpen={proposalModalOpen}
        onClose={() => setProposalModalOpen(false)}
      />
    </>
  )
}

export default function RecruitmentPage() {
  return (
    <RecruitmentContent />
  )
} 